import sanitizeHtml from "sanitize-html";

export const RICH_TEXT_ALLOWED_TAGS = [
  "p",
  "br",
  "ul",
  "ol",
  "li",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "a",
];

/** Strips unsafe or noisy HTML (e.g. pasted from email/Word) while keeping basic formatting and links. */
export const sanitizeRichText = (html: string = ""): string =>
  sanitizeHtml(html, {
    allowedTags: RICH_TEXT_ALLOWED_TAGS,
    allowedAttributes: {
      a: ["href", "target", "rel"],
    },
    allowedSchemes: ["http", "https", "mailto", "tel"],
  }).trim();

/** Visible character count of rich-text HTML (tags removed, whitespace collapsed). */
export const getPlainTextLength = (html: string = ""): number =>
  html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim()
    .length;

export const isRichTextEmpty = (html: string = ""): boolean =>
  getPlainTextLength(html) === 0;

const URL_PATTERN = /(?:https?:\/\/|www\.)[^\s<]*[^\s<.,;:!?)\]'"]/gi;

/**
 * Turns plain-text URLs into anchors and makes every anchor open in a new tab.
 * Expects already-sanitized HTML; text inside existing anchors is left untouched.
 */
export const linkifyHtml = (html: string = ""): string => {
  if (!html || typeof DOMParser === "undefined") return html;
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstElementChild;
  if (!root) return html;

  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    if (!node.parentElement?.closest("a")) textNodes.push(node);
  }

  textNodes.forEach((node) => {
    const text = node.nodeValue || "";
    const fragment = doc.createDocumentFragment();
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    URL_PATTERN.lastIndex = 0;
    while ((match = URL_PATTERN.exec(text)) !== null) {
      const url = match[0];
      if (match.index > lastIndex) {
        fragment.appendChild(doc.createTextNode(text.slice(lastIndex, match.index)));
      }
      const anchor = doc.createElement("a");
      anchor.setAttribute("href", /^https?:\/\//i.test(url) ? url : `https://${url}`);
      anchor.textContent = url;
      fragment.appendChild(anchor);
      lastIndex = match.index + url.length;
    }
    if (lastIndex === 0) return;
    if (lastIndex < text.length) {
      fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
    }
    node.parentNode?.replaceChild(fragment, node);
  });

  root.querySelectorAll("a").forEach((anchor) => {
    anchor.setAttribute("target", "_blank");
    anchor.setAttribute("rel", "noopener noreferrer");
  });

  return root.innerHTML;
};
